import React, { useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Award, BookOpen, CheckCircle, Globe, ShieldCheck, Users, ArrowRight, Star } from 'lucide-react';
import { COURSES } from '../data/mock';
import { useSiteLanguage } from '../context/SiteLanguageContext';
import Seo from '../components/Seo';

const BODIES = {
  othm: {
    code: 'OTHM',
    fullName: 'OTHM Qualifications',
    tagline: 'Ofqual-regulated vocational qualifications trusted across 60+ countries.',
    description:
      'OTHM is an Ofqual-recognised awarding organisation that develops and awards qualifications up to Master\u2019s level. Its vocational qualifications are designed for learners and employers who value practical, career-focused outcomes. OTHM qualifications are widely recognised by UK and international universities for academic progression.',
    intro:
      'At Atlas Academy, we deliver a broad portfolio of OTHM qualifications across Health & Social Care, Business Management, Education, IT, Law, Occupational Health & Safety, and Early Years. Every programme is fully online and tutor-supported.',
    highlights: [
      { icon: ShieldCheck, title: 'Ofqual Regulated', text: 'All OTHM qualifications are regulated by Ofqual and listed on the Regulated Qualifications Framework (RQF).' },
      { icon: Globe, title: 'Global Recognition', text: 'Recognised by hundreds of universities and employers across the UK, Europe, Middle East, Africa and Asia.' },
      { icon: Award, title: 'Progression to University', text: 'Level 4\u20137 OTHM diplomas can fast-track you to the final year of a UK Bachelor\u2019s or onto a Master\u2019s programme.' },
      { icon: Users, title: 'Career-Focused', text: 'Designed with employers in mind \u2014 the content maps directly to sector competence frameworks.' },
    ],
    accent: 'from-[#0f2b4a] to-[#312e81]',
    chip: 'bg-indigo-100 text-indigo-700',
  },
  qualifi: {
    code: 'QUALIFI',
    fullName: 'Qualifi Ltd',
    tagline: 'Innovative Ofqual-regulated qualifications for the modern workforce.',
    description:
      'Qualifi is an Ofqual-regulated awarding organisation specialising in business, management, health, IT, law, and security qualifications from Level 2 through Level 8. Qualifi programmes are built around contemporary practice and designed in close partnership with UK universities, employers and industry experts.',
    intro:
      'Atlas Academy is an approved centre delivering the full Qualifi portfolio online. From entry-level certificates to Level 7 and Level 8 strategic management diplomas, our Qualifi courses equip you with credentials that progress you into senior roles or postgraduate study.',
    highlights: [
      { icon: ShieldCheck, title: 'Ofqual Regulated', text: 'Every Qualifi qualification is on the Regulated Qualifications Framework, carrying official UK credit value.' },
      { icon: Award, title: 'Level 2 to Level 8', text: 'The widest Ofqual-regulated framework spanning introductory to doctoral-equivalent study.' },
      { icon: Globe, title: 'University Partnerships', text: 'Direct progression routes into Bachelor\u2019s top-ups, Master\u2019s and MBA programmes with partner UK universities.' },
      { icon: BookOpen, title: 'Practitioner Designed', text: 'Content authored by subject-matter experts and sector practitioners for immediate workplace relevance.' },
    ],
    accent: 'from-[#1e3a8a] to-[#1d4ed8]',
    chip: 'bg-blue-100 text-blue-700',
  },
};

export default function AwardingBody() {
  const { t } = useSiteLanguage();
  const { code } = useParams();
  const key = (code || '').toLowerCase();
  const body = BODIES[key];

  const courses = useMemo(
    () => (body ? COURSES.filter(c => c.awardingBody === body.code) : []),
    [body]
  );

  const byLevel = useMemo(() => {
    const map = {};
    courses.forEach(c => {
      if (!map[c.level]) map[c.level] = [];
      map[c.level].push(c);
    });
    return Object.keys(map).sort().map(lvl => ({ level: lvl, courses: map[lvl] }));
  }, [courses]);

  if (!body) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title={`${body.code} Qualifications`}
        description={`Explore ${body.code} qualifications available through Atlas Academy — Ofqual-regulated online diplomas with flexible study and payment options.`}
        path={`/awarding-bodies/${key}`}
      />
      {/* Hero */}
      <div className={`bg-gradient-to-br ${body.accent} text-white py-16`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${body.chip}`}>{t('awarding.chip')}</span>
            <span className="text-blue-200 text-xs">{t('awarding.chipLabel')}</span>
          </div>
          <h1 className="text-4xl lg:text-6xl font-extrabold tracking-tight mb-3" data-testid="awarding-body-title">
            {body.fullName}
          </h1>
          <p className="text-blue-100/80 max-w-2xl text-lg leading-relaxed">{body.tagline}</p>
          <div className="flex flex-wrap gap-4 mt-6 text-sm">
            <div className="flex items-center gap-1.5 text-blue-200">
              <BookOpen size={16} /> {courses.length} courses available
            </div>
            <div className="flex items-center gap-1.5 text-blue-200">
              <Star size={16} fill="currentColor" className="text-amber-300" /> {t('awarding.avgRating')}
            </div>
          </div>
        </div>
      </div>

      {/* About the body */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-5">
            <h2 className="text-2xl font-bold text-gray-900">About {body.code}</h2>
            <p className="text-gray-600 leading-relaxed">{body.description}</p>
            <p className="text-gray-600 leading-relaxed">{body.intro}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <h3 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wide">{t('awarding.glance.title')}</h3>
            <ul className="space-y-2.5 text-sm">
              <li className="flex items-center justify-between">
                <span className="text-gray-500">{t('awarding.glance.body')}</span>
                <span className="font-semibold text-gray-800">{body.code}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-gray-500">{t('awarding.glance.regulator')}</span>
                <span className="font-semibold text-gray-800">Ofqual (UK)</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-gray-500">{t('awarding.glance.framework')}</span>
                <span className="font-semibold text-gray-800">RQF</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-gray-500">{t('awarding.glance.levels')}</span>
                <span className="font-semibold text-gray-800">{body.code === 'QUALIFI' ? '2 \u2013 8' : '3 \u2013 7'}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-gray-500">{t('awarding.glance.delivery')}</span>
                <span className="font-semibold text-gray-800">{t('awarding.glance.deliveryValue')}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-white py-12 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Why Choose a {body.code} Qualification</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {body.highlights.map((h, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                <div className={`w-10 h-10 rounded-lg ${body.chip} flex items-center justify-center mb-3`}>
                  <h.icon size={18} />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1.5">{h.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{h.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses by level */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-blue-600 font-semibold text-sm uppercase tracking-wide mb-1">{t('awarding.qualifications.eyebrow')}</p>
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">Browse our {body.code} courses</h2>
          </div>
          <Link
            to={`/courses?awarding=${body.code}`}
            className="hidden sm:flex items-center gap-1.5 text-blue-600 hover:text-blue-800 font-semibold text-sm"
          >
            {t('awarding.qualifications.viewAll')} <ArrowRight size={15} />
          </Link>
        </div>

        {byLevel.length === 0 ? (
          <p className="text-sm text-gray-500">No courses listed yet for {body.code}.</p>
        ) : (
          <div className="space-y-8">
            {byLevel.map(group => (
              <div key={group.level}>
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-3">{group.level}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.courses.map(c => (
                    <Link
                      key={c.slug}
                      to={`/courses/${c.slug}`}
                      className="group bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                      data-testid={`awarding-course-${c.slug}`}
                    >
                      <div className="h-36 overflow-hidden">
                        <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-4">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${body.chip}`}>{c.level}</span>
                        <h4 className="font-semibold text-gray-900 text-sm mt-2 group-hover:text-blue-700 line-clamp-2">{c.title}</h4>
                        <div className="flex items-baseline gap-2 mt-3">
                          <span className="font-bold text-blue-700">£{c.price}</span>
                          {c.onSale && <span className="text-xs text-gray-400 line-through">£{c.originalPrice}</span>}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Other awarding body CTA */}
      <section className="bg-gradient-to-br from-gray-900 to-[#0f2b4a] py-12">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center gap-6 justify-between">
          <div>
            <p className="text-blue-300 text-xs uppercase tracking-wide mb-1.5">{t('awarding.other.eyebrow')}</p>
            <h3 className="text-white text-2xl font-bold">Explore {body.code === 'OTHM' ? 'QUALIFI' : 'OTHM'} qualifications</h3>
            <p className="text-gray-400 text-sm mt-1">{t('awarding.other.desc')}</p>
          </div>
          <Link
            to={`/awarding-bodies/${body.code === 'OTHM' ? 'qualifi' : 'othm'}`}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg text-sm font-bold transition-colors"
            data-testid="awarding-other-cta"
          >
            Visit {body.code === 'OTHM' ? 'QUALIFI' : 'OTHM'} page <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {/* Trust bar */}
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <CheckCircle size={26} className="text-green-600 mx-auto mb-3" />
          <p className="text-sm text-gray-600 leading-relaxed">
            {t('awarding.trust')}
          </p>
        </div>
      </section>
    </div>
  );
}
